import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { generateClient } from 'aws-amplify/data';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const client = generateClient();

const initialState = {
  loading: false,
  error: null,
  chatId: null,
  messages: null,
};

export const fetchChat = createAsyncThunk(
  'messages/fetchChat',
  async ({ reportId }, { rejectWithValue }) => {
    try {
      const response = await client.models.Chat.listChatByReportId({
        reportId,
      });

      return response.data[0].id;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred while fetching messages.');
    }
  }
);

export const fetchMessages = createAsyncThunk(
  'messages/fetchMessages',
  async ({ chatId, limit = 20, nextToken }, { rejectWithValue }) => {
    try {
      const response = await client.models.Message.listMessageByChatIdAndCreatedAt({
        chatId,
        selectionSet: ['id', 'text', 'from.*', 'to.*', 'createdAt'],
        sortDirection: 'ASC',
        limit,
        nextToken,
      });

      return {
        data: response.data,
        nextToken: response.nextToken,
      };
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred while fetching messages.');
    }
  }
);

export const postMessage = createAsyncThunk(
  'messages/postMessage',
  async ({ chatId, text, from, to, tempId, reportId }, { rejectWithValue }) => {
    if (!chatId || !text || !from || !to) {
      return rejectWithValue({
        error: 'Missing required fields. Ensure chatId, text, from, and to are not null.',
        tempId,
      });
    }

    try {
      const createdAt = dayjs().toISOString();

      const response = await client.models.Message.create({
        chatId,
        text,
        fromId: from,
        toId: to,
        createdAt,
      });

      const notificationResponse = await client.models.Notification.create({
        reportId,
        userId: to,
        type: 'MESSAGE',
        isUnread: true,
        title: 'You have new message',
        category: 'New Message',
      });

      if (response.errors && response.errors.length > 0) {
        throw new Error(response.errors[0].message || 'An unknown error occurred.');
      }

      if (!response.data) {
        throw new Error('Server returned null data.');
      }

      return { data: response.data, tempId };
    } catch (error) {
      console.log('Error:', error);
      return rejectWithValue({
        error: error.message || 'An error occurred while posting message.',
        tempId,
      });
    }
  }
);

const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(postMessage.pending, (state, action) => {
        const { chatId, text, from, to } = action.meta.arg;
        const tempId = uuidv4();

        state.messages.data.push({
          id: tempId,
          chatId,
          text,
          from: { id: from },
          to: { id: to },
          createdAt: new Date().toISOString(),
          pending: true,
        });

        action.meta.arg.tempId = tempId;
      })
      .addCase(postMessage.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(postMessage.rejected, (state, action) => {
        state.loading = false;

        const { tempId } = action.payload || {};
        if (tempId) {
          state.messages.data = state.messages.data.filter((msg) => msg.id !== tempId);
        }

        state.error = action.payload?.error || 'Error';
      })

      .addCase(fetchMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchChat.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChat.fulfilled, (state, action) => {
        state.loading = false;
        state.chatId = action.payload;
      })
      .addCase(fetchChat.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default messagesSlice.reducer;
